import { useState } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2, Check, X, RotateCcw, PencilLine } from "lucide-react";
import api from "../lib/api";
import { apiErrorToast } from "../lib/utils";

/**
 * AI-powered resume-summary rewrite flow. Rendered under the analysis results
 * in ResumeAnalyzer. Four states:
 *   idle    → "Would you like us to rewrite your resume summary?" + Yes/No
 *   loading → spinner while AI runs
 *   compare → side-by-side original vs rewritten; Approve / Edit / Discard
 *   applied → confirmation + revert option
 */
export default function SummaryRewrite({ resumeId, analysisId, targetRole, baseSummary, approvedSummary, onApplied }) {
  const [state, setState] = useState(approvedSummary ? "applied" : "idle");
  const [original, setOriginal] = useState("");
  const [rewritten, setRewritten] = useState(approvedSummary || "");
  const [editable, setEditable] = useState(approvedSummary || "");
  const [editing, setEditing] = useState(false);
  const [highlights, setHighlights] = useState([]);
  const [hadExisting, setHadExisting] = useState(true);
  const [appliedAt, setAppliedAt] = useState(null);

  const askRewrite = async () => {
    setState("loading");
    try {
      const r = await api.post("/resume/rewrite-summary", {
        resume_id: resumeId,
        analysis_id: analysisId,
        target_role: targetRole,
      });
      setOriginal(r.data.original_summary || "");
      setHadExisting(Boolean(r.data.had_existing_summary));
      setRewritten(r.data.rewritten_summary || "");
      setEditable(r.data.rewritten_summary || "");
      setHighlights(r.data.highlights || []);
      setState("compare");
    } catch (e) {
      setState("idle");
      apiErrorToast(e, "Could not generate summary. Please try again.");
    }
  };

  const decline = () => {
    toast.success("Keeping your current summary. The rest of the optimization continues normally.");
    setState("declined");
  };

  const approve = async () => {
    try {
      await api.post("/resume/apply-summary", {
        resume_id: resumeId,
        analysis_id: analysisId,
        rewritten_summary: editable,
      });
      setAppliedAt(new Date().toISOString());
      setState("applied");
      toast.success("Summary applied. Your original resume is preserved.");
      onApplied?.(editable);
    } catch (e) {
      apiErrorToast(e, "Failed to apply summary");
    }
  };

  const revert = async () => {
    try {
      await api.post("/resume/revert-summary", {
        resume_id: resumeId,
        analysis_id: analysisId,
      });
      setState("idle");
      setAppliedAt(null);
      setEditable(rewritten); setEditing(false);
      toast.info("Reverted to your original summary.");
      onApplied?.(null);
    } catch (e) {
      apiErrorToast(e, "Failed to revert summary");
    }
  };

  if (!resumeId || !analysisId) return null;

  return (
    <div className="mt-8 pt-6 border-t border-zinc-200" data-testid="summary-rewrite-section">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={16} className="text-indigo-500" />
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">AI Summary Rewrite</div>
      </div>

      {state === "idle" && (
        <div className="rounded-xl border border-indigo-200 bg-gradient-to-br from-indigo-50 via-sky-50 to-emerald-50 p-5" data-testid="summary-rewrite-prompt">
          <div className="font-display font-black text-lg tracking-tight text-zinc-900">
            Would you like us to rewrite your resume summary for <span className="text-indigo-600">{targetRole}</span>?
          </div>
          <p className="text-sm text-zinc-600 mt-1 leading-relaxed">
            We'll tailor it using your actual experience, skills, and achievements — no made-up credentials. You stay in full control.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button data-testid="summary-rewrite-yes" onClick={askRewrite} className="inline-flex items-center gap-2 px-4 py-2 rounded-md btn-gradient text-sm font-semibold hover:opacity-90">
              <Sparkles size={14} /> Yes, rewrite my summary
            </button>
            <button data-testid="summary-rewrite-no" onClick={decline} className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-zinc-300 bg-white text-sm font-semibold text-zinc-700 hover:bg-zinc-50">
              No, keep my current summary
            </button>
          </div>
        </div>
      )}

      {state === "loading" && (
        <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-6 text-center" data-testid="summary-rewrite-loading">
          <Loader2 size={22} className="mx-auto animate-spin text-indigo-500" />
          <div className="font-semibold mt-3 text-sm">Writing your tailored summary…</div>
          <div className="text-xs text-zinc-500 mt-1">Grounded in your actual resume. Takes ~15 seconds.</div>
        </div>
      )}

      {state === "compare" && (
        <div className="space-y-4" data-testid="summary-rewrite-compare">
          {!hadExisting && (
            <div className="text-xs text-zinc-500 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              Your resume didn't have a summary section — the AI generated one from scratch using your experience. Approve to add it.
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-4" data-testid="summary-original">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Original</div>
              <p className="text-sm text-zinc-700 whitespace-pre-wrap leading-relaxed">
                {original ? original : <span className="italic text-zinc-400">No summary found in your uploaded resume.</span>}
              </p>
            </div>
            <div className="rounded-lg border border-indigo-200 bg-white p-4 shadow-sm" data-testid="summary-rewritten">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-[0.2em] text-indigo-500">AI Rewrite · {targetRole}</div>
                <button onClick={() => setEditing(e => !e)} className="text-[11px] text-indigo-600 hover:underline inline-flex items-center gap-1" data-testid="summary-edit-toggle">
                  <PencilLine size={12} /> {editing ? "Done" : "Edit"}
                </button>
              </div>
              {editing ? (
                <textarea
                  data-testid="summary-rewrite-textarea"
                  value={editable}
                  onChange={(e) => setEditable(e.target.value)}
                  rows={7}
                  className="w-full text-sm leading-relaxed border border-indigo-300 rounded-md p-2 focus:outline-none focus:border-indigo-500"
                />
              ) : (
                <p className="text-sm text-zinc-800 whitespace-pre-wrap leading-relaxed">{editable}</p>
              )}
              {highlights?.length > 0 && !editing && (
                <ul className="mt-3 space-y-1">
                  {highlights.map((h, i) => (
                    <li key={i} className="text-[11px] text-zinc-500 flex items-start gap-1.5">
                      <Check size={10} className="text-emerald-500 mt-0.5 shrink-0" /> {h}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button data-testid="summary-approve" onClick={approve} className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700">
              <Check size={14} /> {hadExisting ? "Apply rewritten summary" : "Add this summary"}
            </button>
            <button data-testid="summary-regenerate" onClick={askRewrite} className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-zinc-300 bg-white text-sm font-semibold text-zinc-700 hover:bg-zinc-50">
              <RotateCcw size={14} /> Regenerate
            </button>
            <button data-testid="summary-discard" onClick={() => setState("declined")} className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-zinc-300 bg-white text-sm font-semibold text-zinc-700 hover:bg-zinc-50">
              <X size={14} /> Discard
            </button>
          </div>
        </div>
      )}

      {state === "applied" && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3" data-testid="summary-applied">
          <Check size={18} className="text-emerald-600 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-sm text-emerald-900">Summary applied to your optimized resume.</div>
            <div className="text-xs text-emerald-700 mt-0.5">Your original uploaded resume is preserved. You can revert anytime.</div>
          </div>
          <button data-testid="summary-revert" onClick={revert} className="text-xs text-emerald-700 hover:underline inline-flex items-center gap-1">
            <RotateCcw size={12} /> Revert
          </button>
        </div>
      )}

      {state === "declined" && (
        <div className="rounded-md border border-zinc-200 bg-zinc-50 px-4 py-3 flex items-center justify-between" data-testid="summary-declined">
          <div className="text-sm text-zinc-600">Keeping your current summary.</div>
          <button data-testid="summary-rewrite-reopen" onClick={() => setState("idle")} className="text-xs text-indigo-600 hover:underline">Change my mind</button>
        </div>
      )}
    </div>
  );
}
